import { useCallback, useMemo, useRef, useState } from 'react';
import { useTipProgress } from './TipProgressContext';
import type { TipTour } from './types';

export type ActiveTourState = {
  tour: TipTour;
  stepIndex: number;
};

export const useTourController = (tours: TipTour[]) => {
  const { markTourStep, completeTour, skipTour, resetTour, isTourDone } =
    useTipProgress();
  const [active, setActive] = useState<ActiveTourState | null>(null);
  const activeRef = useRef<ActiveTourState | null>(null);
  activeRef.current = active;

  const tourById = useMemo(() => {
    const map = new Map<string, TipTour>();
    for (const tour of tours) map.set(tour.id, tour);
    return map;
  }, [tours]);

  const startTour = useCallback(
    (tourId: string, fromStep = 0) => {
      const tour = tourById.get(tourId);
      if (!tour || tour.steps.length === 0) return;
      const index = Math.max(0, Math.min(fromStep, tour.steps.length - 1));
      const next = { tour, stepIndex: index };
      activeRef.current = next;
      setActive(next);
      markTourStep(tour.id, tour.steps[index].id, tour.version);
    },
    [tourById, markTourStep]
  );

  const replayTour = useCallback(
    (tourId: string) => {
      resetTour(tourId);
      startTour(tourId, 0);
    },
    [resetTour, startTour]
  );

  const next = useCallback(() => {
    const curr = activeRef.current;
    if (!curr) return;
    const { tour, stepIndex } = curr;
    if (stepIndex >= tour.steps.length - 1) {
      activeRef.current = null;
      setActive(null);
      completeTour(tour.id, tour.version);
      return;
    }
    const nextIndex = stepIndex + 1;
    const nextState = { tour, stepIndex: nextIndex };
    activeRef.current = nextState;
    setActive(nextState);
    markTourStep(tour.id, tour.steps[nextIndex].id, tour.version);
  }, [completeTour, markTourStep]);

  const back = useCallback(() => {
    const curr = activeRef.current;
    if (!curr || curr.stepIndex <= 0) return;
    const nextIndex = curr.stepIndex - 1;
    const nextState = { tour: curr.tour, stepIndex: nextIndex };
    activeRef.current = nextState;
    setActive(nextState);
    markTourStep(
      curr.tour.id,
      curr.tour.steps[nextIndex].id,
      curr.tour.version
    );
  }, [markTourStep]);

  const skip = useCallback(() => {
    const curr = activeRef.current;
    activeRef.current = null;
    setActive(null);
    if (curr) skipTour(curr.tour.id, curr.tour.version);
  }, [skipTour]);

  const stop = useCallback(() => {
    activeRef.current = null;
    setActive(null);
  }, []);

  return {
    active,
    startTour,
    replayTour,
    next,
    back,
    skip,
    stop,
    isTourDone,
  };
};
